import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

export type DeliveryMapCoordinates = [number, number];

export type DeliveryMapOrderPatch = {
  loc_latitude?: number;
  loc_longitude?: number;
  location?: string;
  deliveryAddress?: string;
};

type DeliveryMapPickerProps = {
  initialCenter: DeliveryMapCoordinates;
  selectedPosition: DeliveryMapCoordinates | null;
  selectionLocation?: string | null;
  deliveryAddress?: string | null;
  onUpdateOrderData: (patch: DeliveryMapOrderPatch) => void;
  title: string;
  tapToChoose: string;
  fullscreenTitle: string;
  expand: string;
  collapse: string;
  mapLoading: string;
  mapLoadFailed: string;
  mapUnavailableDescription: string;
  retry: string;
  hasError?: boolean;
  isSelectionEnabled?: boolean;
  preserveControlledAddress?: boolean;
  fieldId?: string;
  previewTestId?: string;
};

/**
 * DeliveryMapCenterSync
 *
 * Keeps a Leaflet viewport aligned with the controlled marker or fallback
 * center. It receives only the active coordinates, depends on the enclosing
 * map instance, and changes the visible viewport without remounting the map.
 */
const DeliveryMapCenterSync: React.FC<{ activePosition: DeliveryMapCoordinates | null }> = ({ activePosition }) => {
  const map = useMap();

  useEffect(() => {
    if (!activePosition) {
      return;
    }

    map.setView(activePosition, map.getZoom(), { animate: true });
  }, [activePosition, map]);

  return null;
};

/**
 * DeliveryMapInteractionBridge
 *
 * Bridges Leaflet interaction events into React callbacks. It receives the
 * interaction mode and selection handlers, depends on the enclosing map, and
 * installs responsive viewport listeners only while the full-screen map is
 * active.
 */
const DeliveryMapInteractionBridge: React.FC<{
  isActive: boolean;
  enablePointSelection: boolean;
  onSelectPosition: (position: DeliveryMapCoordinates) => void;
  onMapReady?: (map: L.Map) => void;
  onZoomChange?: (zoom: number) => void;
}> = ({ isActive, enablePointSelection, onSelectPosition, onMapReady, onZoomChange }) => {
  const map = useMapEvents({
    click(event) {
      if (enablePointSelection) {
        onSelectPosition([event.latlng.lat, event.latlng.lng]);
      }
    },
    zoomend() {
      onZoomChange?.(map.getZoom());
    },
  });

  useEffect(() => {
    onMapReady?.(map);
  }, [map, onMapReady]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    const invalidateMapSize = () => map.invalidateSize({ animate: false });
    const timeoutIds = [
      window.setTimeout(invalidateMapSize, 50),
      window.setTimeout(invalidateMapSize, 250),
    ];

    invalidateMapSize();
    window.addEventListener('resize', invalidateMapSize);
    window.addEventListener('orientationchange', invalidateMapSize);
    window.visualViewport?.addEventListener('resize', invalidateMapSize);

    return () => {
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
      window.removeEventListener('resize', invalidateMapSize);
      window.removeEventListener('orientationchange', invalidateMapSize);
      window.visualViewport?.removeEventListener('resize', invalidateMapSize);
    };
  }, [isActive, map]);

  return null;
};

/**
 * DeliveryMapLocationMarker
 *
 * Renders the controlled delivery marker for preview and full-screen maps. It
 * receives an optional position, has no external data dependencies, and does
 * not capture keyboard or pointer interaction from the map surface.
 */
const DeliveryMapLocationMarker: React.FC<{
  position: DeliveryMapCoordinates | null;
}> = ({ position }) => (
  position ? <Marker position={position} autoPanOnFocus={false} keyboard={false} /> : null
);

/**
 * DeliveryMapFrame
 *
 * Renders one OpenStreetMap-backed Leaflet instance for either the embedded
 * preview or the full-screen picker. It receives controlled center, marker,
 * zoom, and lifecycle callbacks; its side effects are tile loading and map
 * interaction events delegated to the parent picker.
 */
const DeliveryMapFrame: React.FC<{
  height: React.CSSProperties['height'];
  center: DeliveryMapCoordinates;
  position: DeliveryMapCoordinates | null;
  onSelectPosition: (position: DeliveryMapCoordinates) => void;
  isInteractive?: boolean;
  enablePointSelection?: boolean;
  zoom: number;
  onZoomChange?: (zoom: number) => void;
  onMapReady?: (map: L.Map) => void;
  onTilesLoading?: () => void;
  onTilesReady?: () => void;
  onTilesError?: () => void;
}> = ({
  height,
  center,
  position,
  onSelectPosition,
  isInteractive = true,
  enablePointSelection = true,
  zoom,
  onZoomChange,
  onMapReady,
  onTilesLoading,
  onTilesReady,
  onTilesError,
}) => (
  <MapContainer
    center={center}
    zoom={zoom}
    zoomControl={false}
    style={{ height, width: '100%' }}
    dragging={isInteractive}
    touchZoom={isInteractive ? 'center' : false}
    doubleClickZoom={isInteractive}
    scrollWheelZoom={isInteractive}
    boxZoom={isInteractive}
    keyboard={isInteractive}
    className="rounded"
  >
    <TileLayer
      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      attribution="&copy; OpenStreetMap contributors"
      eventHandlers={{
        loading: () => onTilesLoading?.(),
        load: () => onTilesReady?.(),
        tileerror: () => onTilesError?.(),
      }}
    />
    <DeliveryMapCenterSync activePosition={position ?? center} />
    <DeliveryMapInteractionBridge
      isActive={isInteractive}
      enablePointSelection={enablePointSelection}
      onSelectPosition={onSelectPosition}
      onMapReady={onMapReady}
      onZoomChange={onZoomChange}
    />
    <DeliveryMapLocationMarker position={position} />
  </MapContainer>
);

/**
 * DeliveryMapPicker
 *
 * Provides the shared checkout and staff delivery-point experience: a static
 * OpenStreetMap preview opens into a mobile-safe full-screen picker. It receives
 * controlled coordinates, localized labels, and a typed patch callback; it
 * depends on Leaflet tiles and Nominatim reverse geocoding, and reports exact
 * coordinates immediately while applying only the latest resolved address.
 */
const DeliveryMapPicker = React.memo(({
  initialCenter,
  selectedPosition,
  selectionLocation,
  deliveryAddress,
  onUpdateOrderData,
  title,
  tapToChoose,
  fullscreenTitle,
  expand,
  collapse,
  mapLoading,
  mapLoadFailed,
  mapUnavailableDescription,
  retry,
  hasError,
  isSelectionEnabled = true,
  preserveControlledAddress = false,
  fieldId = 'checkout-delivery-point-field',
  previewTestId = 'delivery-map-preview',
}: DeliveryMapPickerProps) => {
  const DELIVERY_MAP_DEFAULT_ZOOM = 14;
  const DELIVERY_MAP_SELECTED_ZOOM = 17;
  const DELIVERY_MAP_LOAD_TIMEOUT_MS = 8000;
  const DELIVERY_MAP_GESTURE_SHIELD_TIMEOUT_MS = 700;
  const updateOrderDataRef = useRef(onUpdateOrderData);
  const preservedInitialPositionRef = useRef<string | null>(
    preserveControlledAddress && selectedPosition && deliveryAddress?.trim()
      ? `${selectedPosition[0]},${selectedPosition[1]}`
      : null,
  );
  const previewMapRef = useRef<HTMLDivElement | null>(null);
  const fullscreenMapRef = useRef<L.Map | null>(null);
  const geocodeRequestIdRef = useRef(0);
  const fullscreenLoadTimeoutRef = useRef<number | null>(null);
  const fullscreenLayoutTimeoutRef = useRef<number | null>(null);
  const previewGestureShieldTimeoutRef = useRef<number | null>(null);
  const previewGestureOpenedRef = useRef(false);
  const hasUserSelectedPositionRef = useRef(false);
  const [position, setPosition] = useState<DeliveryMapCoordinates | null>(selectedPosition);
  const [currentZoom, setCurrentZoom] = useState<number>(
    selectedPosition ? DELIVERY_MAP_SELECTED_ZOOM : DELIVERY_MAP_DEFAULT_ZOOM
  );
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [fullscreenMapStatus, setFullscreenMapStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [fullscreenMapRetryKey, setFullscreenMapRetryKey] = useState<number>(0);
  const [fullscreenMapHeight, setFullscreenMapHeight] = useState<string>('calc(var(--tg-viewport-height, 100vh) - 86px)');
  const [isPreviewGestureShieldActive, setIsPreviewGestureShieldActive] = useState<boolean>(false);

  useEffect(() => {
    updateOrderDataRef.current = onUpdateOrderData;
  }, [onUpdateOrderData]);

  useEffect(() => {
    if (!selectedPosition) {
      setPosition((previous) => (previous === null ? previous : null));
      setCurrentZoom(DELIVERY_MAP_DEFAULT_ZOOM);
      return;
    }

    setPosition((previous) => (
      previous && previous[0] === selectedPosition[0] && previous[1] === selectedPosition[1]
        ? previous
        : selectedPosition
    ));

    setCurrentZoom((previousZoom) => (
      previousZoom === DELIVERY_MAP_DEFAULT_ZOOM ? DELIVERY_MAP_SELECTED_ZOOM : previousZoom
    ));
  }, [selectedPosition]);

  const handleSelectPosition = useCallback((nextPosition: DeliveryMapCoordinates) => {
    hasUserSelectedPositionRef.current = true;
    setPosition((previous) => (
      previous && previous[0] === nextPosition[0] && previous[1] === nextPosition[1]
        ? previous
        : nextPosition
    ));
    setCurrentZoom((previousZoom) => Math.max(previousZoom, DELIVERY_MAP_SELECTED_ZOOM));
  }, []);

  const clearFullscreenLoadTimeout = useCallback(() => {
    if (fullscreenLoadTimeoutRef.current != null) {
      window.clearTimeout(fullscreenLoadTimeoutRef.current);
      fullscreenLoadTimeoutRef.current = null;
    }
  }, []);

  const clearFullscreenLayoutTimeout = useCallback(() => {
    if (fullscreenLayoutTimeoutRef.current != null) {
      window.clearTimeout(fullscreenLayoutTimeoutRef.current);
      fullscreenLayoutTimeoutRef.current = null;
    }
  }, []);

  const scheduleFullscreenLoadTimeout = useCallback(() => {
    clearFullscreenLoadTimeout();
    fullscreenLoadTimeoutRef.current = window.setTimeout(() => {
      setFullscreenMapStatus((previousStatus) => (
        previousStatus === 'ready' ? previousStatus : 'error'
      ));
    }, DELIVERY_MAP_LOAD_TIMEOUT_MS);
  }, [clearFullscreenLoadTimeout]);

  const clearPreviewGestureShieldTimeout = useCallback(() => {
    if (previewGestureShieldTimeoutRef.current != null) {
      window.clearTimeout(previewGestureShieldTimeoutRef.current);
      previewGestureShieldTimeoutRef.current = null;
    }
  }, []);

  const hidePreviewGestureShield = useCallback(() => {
    clearPreviewGestureShieldTimeout();
    setIsPreviewGestureShieldActive(false);
  }, [clearPreviewGestureShieldTimeout]);

  const showPreviewGestureShield = useCallback(() => {
    clearPreviewGestureShieldTimeout();
    setIsPreviewGestureShieldActive(true);
    previewGestureShieldTimeoutRef.current = window.setTimeout(() => {
      previewGestureShieldTimeoutRef.current = null;
      setIsPreviewGestureShieldActive(false);
    }, DELIVERY_MAP_GESTURE_SHIELD_TIMEOUT_MS);
  }, [clearPreviewGestureShieldTimeout]);

  const syncFullscreenMapLayout = useCallback(() => {
    const viewportHeight = Math.max(window.visualViewport?.height ?? window.innerHeight, 320);
    setFullscreenMapHeight(`calc(${Math.round(viewportHeight)}px - 86px)`);
    clearFullscreenLayoutTimeout();

    const map = fullscreenMapRef.current;
    if (!map) {
      return;
    }

    map.invalidateSize({ animate: false });
    fullscreenLayoutTimeoutRef.current = window.setTimeout(() => {
      fullscreenLayoutTimeoutRef.current = null;
      map.invalidateSize({ animate: false });
    }, 200);
  }, [clearFullscreenLayoutTimeout]);

  const openFullscreenMap = useCallback(() => {
    if (!isSelectionEnabled) {
      return;
    }
    setFullscreenMapStatus('loading');
    setIsFullscreen(true);
  }, [isSelectionEnabled]);

  const retryFullscreenMap = useCallback(() => {
    setFullscreenMapStatus('loading');
    setFullscreenMapRetryKey((previous) => previous + 1);
  }, []);

  useEffect(() => {
    const previewMap = previewMapRef.current;
    if (!previewMap || !isSelectionEnabled) {
      return undefined;
    }

    /**
     * Native non-passive listeners stop a two-finger gesture from leaking to
     * browser zoom while the preview transitions into the full-screen picker.
     */
    const handlePreviewGesture = (event: TouchEvent) => {
      if (event.touches.length < 2) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      showPreviewGestureShield();

      if (!previewGestureOpenedRef.current) {
        previewGestureOpenedRef.current = true;
        openFullscreenMap();
      }
    };

    const resetPreviewGesture = () => {
      previewGestureOpenedRef.current = false;
      hidePreviewGestureShield();
    };

    const listenerOptions: AddEventListenerOptions = { passive: false };
    previewMap.addEventListener('touchstart', handlePreviewGesture, listenerOptions);
    previewMap.addEventListener('touchmove', handlePreviewGesture, listenerOptions);
    previewMap.addEventListener('touchend', resetPreviewGesture);
    previewMap.addEventListener('touchcancel', resetPreviewGesture);

    return () => {
      previewMap.removeEventListener('touchstart', handlePreviewGesture, listenerOptions);
      previewMap.removeEventListener('touchmove', handlePreviewGesture, listenerOptions);
      previewMap.removeEventListener('touchend', resetPreviewGesture);
      previewMap.removeEventListener('touchcancel', resetPreviewGesture);
      clearPreviewGestureShieldTimeout();
    };
  }, [clearPreviewGestureShieldTimeout, hidePreviewGestureShield, isSelectionEnabled, openFullscreenMap, showPreviewGestureShield]);

  useEffect(() => {
    if (!isFullscreen) {
      clearFullscreenLoadTimeout();
      clearFullscreenLayoutTimeout();
      fullscreenMapRef.current = null;
      return;
    }

    syncFullscreenMapLayout();
    scheduleFullscreenLoadTimeout();

    return () => {
      clearFullscreenLoadTimeout();
      clearFullscreenLayoutTimeout();
    };
  }, [clearFullscreenLayoutTimeout, clearFullscreenLoadTimeout, isFullscreen, scheduleFullscreenLoadTimeout, syncFullscreenMapLayout, fullscreenMapRetryKey]);

  useEffect(() => {
    if (!position) {
      return;
    }

    const requestId = geocodeRequestIdRef.current + 1;
    geocodeRequestIdRef.current = requestId;
    const controller = new AbortController();

    const fetchAddress = async () => {
      updateOrderDataRef.current({
        loc_latitude: position[0],
        loc_longitude: position[1],
        ...(selectionLocation ? { location: selectionLocation } : {}),
      });

      /**
       * A restored staff draft can contain an address that was manually
       * corrected after geocoding. Keep that controlled value while the point
       * is unchanged; an actual map selection still requests a fresh address.
       * The default remains false so public checkout behavior is unchanged.
       */
      if (!hasUserSelectedPositionRef.current
        && preservedInitialPositionRef.current === `${position[0]},${position[1]}`) {
        return;
      }

      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${position[0]}&lon=${position[1]}&format=json&accept-language=en`,
          { signal: controller.signal }
        );
        const data: unknown = await response.json();
        const nextAddress = typeof data === 'object' && data !== null && !Array.isArray(data)
          && typeof (data as Record<string, unknown>).display_name === 'string'
          ? ((data as Record<string, unknown>).display_name as string).trim()
          : '';
        if (geocodeRequestIdRef.current !== requestId || nextAddress.length === 0) {
          return;
        }

        updateOrderDataRef.current({ deliveryAddress: nextAddress });
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          return;
        }
      }
    };

    void fetchAddress();
    return () => controller.abort();
  }, [position, selectionLocation]);

  return (
    <div className="card shadow-sm border-0 mb-3">
      <div className="card-body p-3">
        <div className="fw-semibold mb-2">{title}</div>
        <div className="small text-secondary mb-2">{tapToChoose}</div>
        <div
          ref={previewMapRef}
          data-testid={previewTestId}
          className={`position-relative w-100 rounded overflow-hidden border ${hasError ? 'border-danger border-2' : 'border-light-subtle'}`}
          id={fieldId}
        >
          <DeliveryMapFrame
            height={220}
            center={initialCenter}
            position={position}
            onSelectPosition={handleSelectPosition}
            isInteractive={false}
            enablePointSelection={false}
            zoom={currentZoom}
          />
          <button
            type="button"
            className="btn position-absolute top-0 start-0 w-100 h-100 border-0 p-0 rounded d-flex align-items-end justify-content-center pb-3"
            style={{ zIndex: 900, background: 'transparent', touchAction: 'manipulation' }}
            aria-label={`${expand}. ${tapToChoose}`}
            disabled={!isSelectionEnabled}
            onClick={openFullscreenMap}
          >
            <span
              data-testid="delivery-map-expand-hint"
              className="badge rounded-pill text-bg-light border shadow-sm px-3 py-2 fw-semibold"
            >
              {expand}
            </span>
          </button>
        </div>
      </div>

      <Modal
        show={isFullscreen}
        onHide={() => {
          hidePreviewGestureShield();
          clearFullscreenLayoutTimeout();
          fullscreenMapRef.current = null;
          setIsFullscreen(false);
        }}
        fullscreen
        onEntered={syncFullscreenMapLayout}
        onExited={() => {
          clearFullscreenLayoutTimeout();
          fullscreenMapRef.current = null;
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{fullscreenTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 overflow-hidden">
          <div
            data-testid="delivery-map-fullscreen"
            data-map-zoom={currentZoom}
            className="position-relative w-100"
            style={{ height: fullscreenMapHeight }}
          >
            <DeliveryMapFrame
              key={fullscreenMapRetryKey}
              height="100%"
              center={initialCenter}
              position={position}
              onSelectPosition={handleSelectPosition}
              isInteractive
              enablePointSelection
              zoom={currentZoom}
              onZoomChange={setCurrentZoom}
              onMapReady={(map) => {
                fullscreenMapRef.current = map;
              }}
              onTilesLoading={() => {
                setFullscreenMapStatus((previousStatus) => (
                  previousStatus === 'ready' ? previousStatus : 'loading'
                ));
                scheduleFullscreenLoadTimeout();
              }}
              onTilesReady={() => {
                clearFullscreenLoadTimeout();
                setFullscreenMapStatus('ready');
              }}
              onTilesError={() => {
                clearFullscreenLoadTimeout();
                setFullscreenMapStatus((previousStatus) => (
                  previousStatus === 'ready' ? previousStatus : 'error'
                ));
              }}
            />
            {isPreviewGestureShieldActive && (
              <div
                data-testid="delivery-map-gesture-shield"
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{ zIndex: 990, backgroundColor: 'transparent', touchAction: 'none' }}
                aria-hidden="true"
              />
            )}
            <div className="position-absolute top-0 start-0 m-3 d-flex flex-column gap-2" style={{ zIndex: 1000 }}>
              <Button
                variant="light"
                className="rounded-circle border shadow-sm d-flex align-items-center justify-content-center p-0"
                style={{ width: '46px', height: '46px' }}
                onClick={() => fullscreenMapRef.current?.zoomIn()}
              >
                <span className="fs-4 lh-1">+</span>
              </Button>
              <Button
                variant="light"
                className="rounded-circle border shadow-sm d-flex align-items-center justify-content-center p-0"
                style={{ width: '46px', height: '46px' }}
                onClick={() => fullscreenMapRef.current?.zoomOut()}
              >
                <span className="fs-4 lh-1">−</span>
              </Button>
            </div>
            {fullscreenMapStatus !== 'ready' && (
              <div
                data-testid="delivery-map-status-overlay"
                className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
                style={{ zIndex: 950, backgroundColor: 'rgba(247, 248, 245, 0.82)' }}
              >
                {fullscreenMapStatus === 'error' ? (
                  <div className="bg-white rounded-4 shadow-sm border p-3 text-center" style={{ maxWidth: '320px' }}>
                    <div className="fw-semibold mb-2">{mapLoadFailed}</div>
                    <div className="small text-secondary mb-3">{mapUnavailableDescription}</div>
                    <Button variant="success" className="rounded-pill px-4 fw-semibold" onClick={retryFullscreenMap}>
                      {retry}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-white rounded-4 shadow-sm border px-4 py-3 d-flex align-items-center gap-3">
                    <Spinner animation="border" size="sm" />
                    <span className="fw-semibold text-secondary">{mapLoading}</span>
                  </div>
                )}
              </div>
            )}
            <Button
              variant="success"
              data-testid="delivery-map-collapse"
              className="rounded-pill position-absolute end-0 me-3 shadow-lg px-4 py-3 fw-bold text-white"
              style={{ zIndex: 1000, bottom: '20px', minHeight: '58px', minWidth: '156px' }}
              onClick={() => setIsFullscreen(false)}
            >
              {collapse}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
});

DeliveryMapPicker.displayName = 'DeliveryMapPicker';

export default DeliveryMapPicker;
